import { useEffect, useState } from 'react';
import { Card, ConfirmModal, Field, Skeleton } from '../../components/UI';
import { useToast } from '../../components/Toast';
import { archiveAccount, deleteAccount, listenAccounts, restoreAccount, saveAccount } from '../../firebase/repo';
import type { Account, AccountType } from '../../types';
import { formatMoney } from '../../utils/date';
import { AccountLedgerModal, AccountTools } from './AccountTools';

const accountTypes: AccountType[] = ['bank', 'cash', 'wallet', 'credit_card'];

const emptyDraft: Partial<Account> = {
  name: '',
  type: 'bank',
  institutionName: '',
  last4: '',
  openingBalance: 0,
  currentBalance: 0,
  creditLimit: 0,
  active: true,
  status: 'active'
};

export function AccountManager({ uid, accounts: providedAccounts }: { uid: string; accounts?: Account[] }) {
  const toast = useToast();
  const [localAccounts, setLocalAccounts] = useState<Account[]>([]);
  const [draft, setDraft] = useState<Partial<Account>>(emptyDraft);
  const [formOpen, setFormOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Account | null>(null);
  const [ledgerAccount, setLedgerAccount] = useState<Account | null>(null);

  useEffect(() => {
    if (providedAccounts) {
      setLoading(false);
      return undefined;
    }
    return listenAccounts(uid, (rows) => { setLocalAccounts(rows); setLoading(false); });
  }, [uid, providedAccounts]);

  const accounts = providedAccounts || localAccounts;

  const startAdd = (type: AccountType = 'bank') => {
    const next = { ...emptyDraft, type };
    setDraft(next);
    setFormOpen(true);
  };

  const startEdit = (account: Account) => {
    setDraft(account);
    setFormOpen(true);
  };

  const closeForm = () => {
    setDraft({ ...emptyDraft });
    setFormOpen(false);
  };

  const updateDraft = (patch: Partial<Account>) => {
    const next = { ...draft, ...patch };
    if (patch.type && !draft.id) {
      next.currentBalance = Number(next.openingBalance || 0);
      next.availableLimit = patch.type === 'credit_card' ? Number(next.creditLimit || 0) - Number(next.currentBalance || 0) : 0;
    }
    setDraft(next);
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const openingBalance = Number(draft.openingBalance || 0);
      const currentBalance = draft.id ? Number(draft.currentBalance || 0) : openingBalance;
      await saveAccount(uid, { ...draft, openingBalance, currentBalance });
      closeForm();
      toast('Account saved');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Account save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const archive = async (account: Account) => {
    await archiveAccount(uid, account.id);
    toast('Account archived');
  };

  const restore = async (account: Account) => {
    await restoreAccount(uid, account.id);
    toast('Account restored');
  };

  const remove = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteAccount(uid, deleteTarget.id);
      setDeleteTarget(null);
      toast('Account deleted');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Account delete failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const activeCount = accounts.filter((account) => account.status !== 'archived' && account.active !== false).length;
  const cardCount = accounts.filter((account) => account.type === 'credit_card').length;

  return <Card className="settings-manager-card">
    <div className="section-title">
      <div><h2>Accounts & cards</h2><p className="muted">Bank, cash, wallet and credit card balances.</p></div>
      <div className="row-actions"><button className="btn small primary" onClick={() => startAdd('bank')}>Add account</button><button className="btn small" onClick={() => startAdd('credit_card')}>Add card</button></div>
    </div>
    <div className="settings-summary-strip">
      <span><b>{activeCount}</b> active</span>
      <span><b>{cardCount}</b> cards</span>
      <span><b>{accounts.length}</b> saved</span>
    </div>
    {formOpen && <div className="settings-form-panel">
      <div className="section-title compact"><div><h3>{draft.id ? 'Edit account' : 'New account'}</h3><p className="muted">Balances update from future account-linked transactions.</p></div><button className="btn small" onClick={closeForm}>Close</button></div>
      <div className="grid2">
        <Field label="Name"><input value={draft.name || ''} onChange={(e) => updateDraft({ name: e.target.value })} placeholder="HDFC savings, Cash, SBI card" /></Field>
        <Field label="Type"><select value={draft.type || 'bank'} onChange={(e) => updateDraft({ type: e.target.value as AccountType })}>{accountTypes.map((type) => <option key={type} value={type}>{type.replace('_', ' ')}</option>)}</select></Field>
      </div>
      <div className="grid2">
        <Field label="Institution"><input value={draft.institutionName || ''} onChange={(e) => updateDraft({ institutionName: e.target.value })} placeholder="Bank/card/wallet name" /></Field>
        <Field label="Last 4"><input value={draft.last4 || ''} maxLength={4} onChange={(e) => updateDraft({ last4: e.target.value.replace(/\D/g, '').slice(0, 4) })} placeholder="Optional" /></Field>
      </div>
      <div className="grid2">
        <Field label="Opening balance"><input type="number" inputMode="decimal" value={draft.openingBalance || ''} onChange={(e) => updateDraft({ openingBalance: Number(e.target.value), currentBalance: draft.id ? draft.currentBalance : Number(e.target.value) })} /></Field>
        <Field label={draft.type === 'credit_card' ? 'Outstanding/current' : 'Current balance'}><input type="number" inputMode="decimal" value={draft.currentBalance || ''} onChange={(e) => updateDraft({ currentBalance: Number(e.target.value) })} /></Field>
      </div>
      {draft.type === 'credit_card' && <div className="grid2">
        <Field label="Credit limit"><input type="number" inputMode="decimal" value={draft.creditLimit || ''} onChange={(e) => updateDraft({ creditLimit: Number(e.target.value), availableLimit: Number(e.target.value) - Number(draft.currentBalance || 0) })} /></Field>
        <Field label="Available limit"><input type="number" inputMode="decimal" value={draft.availableLimit || ''} onChange={(e) => updateDraft({ availableLimit: Number(e.target.value) })} /></Field>
      </div>}
      <button className="btn primary wide" disabled={saving || !draft.name} onClick={save}>{saving ? 'Saving...' : draft.id ? 'Update account' : 'Save account'}</button>
    </div>}
    <AccountTools uid={uid} accounts={accounts} />
    {loading ? <Skeleton /> : accounts.map((account) => <div className="account-row" key={account.id}>
      <div><b>{account.name}</b><span>{account.type.replace('_', ' ')} / {account.institutionName || '-'}{account.last4 ? ` / ${account.last4}` : ''}</span></div>
      <div className="right"><strong>{formatMoney(account.currentBalance)}</strong>{account.type === 'credit_card' && <span>Available {formatMoney(account.availableLimit || 0)}</span>}<div className="row-actions"><button className="btn small" onClick={() => setLedgerAccount(account)}>Ledger</button><details className="overflow-menu"><summary>More</summary><div><button className="btn small" onClick={() => startEdit(account)}>Edit</button>{account.status === 'archived' ? <button className="btn small" onClick={() => restore(account)}>Restore</button> : <button className="btn small" onClick={() => archive(account)}>Archive</button>}<button className="btn small danger" onClick={() => setDeleteTarget(account)}>Del</button></div></details></div></div>
    </div>)}
    {!loading && !accounts.length && <p className="empty-state">No accounts added yet.</p>}
    <ConfirmModal open={!!deleteTarget} title="Delete account?" message="This soft deletes the account. Existing transactions keep their saved account label." tone="danger" confirmLabel="Delete" loading={saving} details={deleteTarget ? [{ label: 'Account', value: deleteTarget.name }, { label: 'Balance', value: formatMoney(deleteTarget.currentBalance) }] : []} onCancel={() => setDeleteTarget(null)} onConfirm={remove} />
    <AccountLedgerModal uid={uid} account={ledgerAccount} accounts={accounts} open={!!ledgerAccount} onClose={() => setLedgerAccount(null)} />
  </Card>;
}
